// Inline SVG icon set. Hoisted at module scope (rendering-hoist-jsx).
// All icons use stroke="currentColor" so they pick up the surrounding text color.

import type { SVGProps } from "react";

const baseProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export function ArrowUpRight(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M4.5 11.5 11.5 4.5" />
      <path d="M5 4.5h6.5V11" />
    </svg>
  );
}

export function LinkIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 1 0-4.2-4.2l-.6.6" />
      <path d="M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 1 0 4.2 4.2l.6-.6" />
    </svg>
  );
}

export function ChevronDown(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

export function MailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <rect x="2" y="3" width="12" height="10" rx="2" />
      <path d="m2.5 4.5 5.5 4 5.5-4" />
    </svg>
  );
}

export function PhoneIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 3h2.5l1 3-1.5 1a8 8 0 0 0 4 4l1-1.5 3 1V13a1 1 0 0 1-1 1 11 11 0 0 1-10-10 1 1 0 0 1 1-1Z" />
    </svg>
  );
}

export function PinIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M8 13.5s4.5-3.6 4.5-7a4.5 4.5 0 0 0-9 0c0 3.4 4.5 7 4.5 7Z" />
      <circle cx="8" cy="6.5" r="1.5" />
    </svg>
  );
}

export function HamburgerIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M2.5 4h11" />
      <path d="M2.5 8h11" />
      <path d="M2.5 12h11" />
    </svg>
  );
}

export function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="m4 4 8 8" />
      <path d="m12 4-8 8" />
    </svg>
  );
}

export function CheckIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="m3 8.5 3.5 3 6.5-7" />
    </svg>
  );
}

export function SparkleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M8 2v4" />
      <path d="M8 10v4" />
      <path d="M2 8h4" />
      <path d="M10 8h4" />
    </svg>
  );
}

export function BoltIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M9 2 3.5 9h3.5L7 14l5.5-7H9V2Z" />
    </svg>
  );
}

export function HeartIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M8 13.5S2.5 10 2.5 6a2.8 2.8 0 0 1 5.5-1 2.8 2.8 0 0 1 5.5 1c0 4-5.5 7.5-5.5 7.5Z" />
    </svg>
  );
}

export function LinkedInIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props} fill="currentColor" stroke="none">
      <path d="M3.5 5.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3ZM2 6.5h3v7H2v-7ZM6 6.5h2.8V8c.5-.9 1.7-1.5 2.7-1.5 2 0 2.5 1.3 2.5 3v4h-3v-3.5c0-.9-.4-1.5-1.2-1.5s-1.3.5-1.3 1.5V13.5H6V6.5Z" />
    </svg>
  );
}

export function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props} fill="currentColor" stroke="none">
      <path d="m9.5 7 4.4-5h-1.4l-3.7 4.3L5.6 2H2l4.7 7-4.7 5h1.5l4-4.6L10.4 14H14l-4.5-7Zm-1.4 1.6-.5-.7L4 3h1.6l3 4.4.5.7 4 5.5h-1.6l-3.4-4.5Z" />
    </svg>
  );
}

export function FacebookIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props} fill="currentColor" stroke="none">
      <path d="M9.5 14V9h1.7l.3-2H9.5V5.7c0-.6.2-1 1-1H11.5V3h-2c-1.7 0-2.5 1-2.5 2.4V7H5.5v2H7v5h2.5Z" />
    </svg>
  );
}

export function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <circle cx="7" cy="7" r="4.5" />
      <path d="m10.5 10.5 3 3" />
    </svg>
  );
}

export function PlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M8 3v10" />
      <path d="M3 8h10" />
    </svg>
  );
}

export function MinusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 8h10" />
    </svg>
  );
}

export function ArrowRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...baseProps} {...props}>
      <path d="M3 8h10" />
      <path d="m9 4 4 4-4 4" />
    </svg>
  );
}
